import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useMutation } from "@apollo/react-hooks";
import { Box, Collapse } from "@mui/material";
import useStyles from "./data-edit-collapse.style";
import {
  MyHrDataChangeBox,
  MyHrDataEditCardItem,
  MyHrDataEditCollapseHeader,
  MyHrDataRequesAttachment,
} from "my-hr/components";
import {
  permanentAddress,
  serviceAddress,
  temporaryAddress,
} from "my-hr/configs/my-hr-info-card-defs";
import {
  MY_HR_APPROVE_CHANGE_REQUEST,
  MY_HR_DELETE_CHANGE_REQUEST,
  MY_HR_REJECT_CHANGE_REQUEST,
} from "my-hr/graphql";
import {
  useMyHrChangeRequests,
  useMyHrEmployee,
  useMyHrUserRolesDataByContract,
} from "my-hr/hooks";
import {
  OcCardButtonBar,
  OcInfoCard,
  OcRequestsMessagesCard,
} from "oc/components";
import {
  useOcClientState,
  useOcCurrentUserState,
  useOcIsMobile,
  useOcSnackbar,
} from "oc/hooks";
import getCountries from "oc/utils/get-countries";
import getNationalities from "oc/utils/get-nationalities";
import { OcConfirmationDialog } from "oc/components";

type MyHrDataEditCollapseProps = {
  oldValue: any;
  newValue: any;
  type: string;
  createdDateAt: string;
  validDateFrom: string;
  messages: any[];
  approvedAt: string | null;
  rejectedAt: string | null;
  selectedId: string;
  typeGroup: string;
  employeeCode: string;
  isAdmin: boolean;
  typeGroupId: string;
  disabled: boolean;
};

export const MyHrDataEditCollapse = ({
  oldValue,
  newValue,
  type,
  createdDateAt,
  validDateFrom,
  messages,
  approvedAt,
  rejectedAt,
  selectedId,
  typeGroup,
  employeeCode,
  isAdmin,
  typeGroupId,
  disabled,
}: MyHrDataEditCollapseProps) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  let { contractCode } = useOcCurrentUserState();
  const countries = getCountries(i18n.language);
  const nationalities = getNationalities(i18n.language);
  const [message] = useOcClientState("newMessageText", "");
  const isMobile = useOcIsMobile();

  const { data: rolesData } = useMyHrUserRolesDataByContract({
    contract: contractCode,
  });

  const isMyHrExaminer = rolesData.find(
    (item: { product: string; role: string }) =>
      item.product === "MyHR" && item.role === "examiner",
  );

  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showRejectedConfirmDialog, setShowRejectedConfirmDialog] = useState(false);

  const { data: allDataTypeGroup, refetch } =
    useMyHrChangeRequests(employeeCode);

  const { data: employeeData } = useMyHrEmployee({ employeeId: employeeCode });

  const { setOpenMessage } = useOcSnackbar();

  const myRequests = history?.location?.pathname === "/my-hr/my-requests";

  const [deleteChangeRequest] = useMutation(MY_HR_DELETE_CHANGE_REQUEST, {
    onCompleted: () => {
      refetch();

      const targetPath = isMyHrExaminer && !myRequests
        ? `/my-hr/view-requests`
        : `/my-hr/my-requests`;

      // Csak akkor navigálj, ha más az útvonal
      if (history.location.pathname !== targetPath) {
        history.push(targetPath);
      }

      setOpenMessage({
        type: "success",
        message: t("MY_HR_DELETE_REQUEST"),
      });
      setLoading(false);
    },
    onError: (error) => {
      if (error.message === "Import process cancelled") {
        console.log(error);
      }
      setOpenMessage({
        type: "error",
        message: t("MY_HR_DELETE_REQUEST_ERROR"),
      });
    },
  });

  const [approveChangeRequest] = useMutation(MY_HR_APPROVE_CHANGE_REQUEST, {
    onCompleted: (data) => {
      if (data?.approveChangeRequest?.result) {
        refetch();
        setOpenMessage({
          type: "success",
          message: t("MY_HR_CHANGE_REQUEST_SUCCEEDED"),
        });
        setLoading(false);
        //history.push(`/my-hr/view-requests`);
      } else {
        setOpenMessage({
          type: "error",
          message: data?.approveChangeRequest?.messages?.[0] || t("OC_ERROR"),
        });
      }
    },
    onError: (error) => {
      console.log("send error message", error);
    },
  });

  const [rejectChangeRequest] = useMutation(MY_HR_REJECT_CHANGE_REQUEST, {
    onCompleted: () => {
      refetch();
      setOpenMessage({
        type: "success",
        message: t("MY_HR_CHANGE_REQUEST_REJECTED"),
      });
      setLoading(false);
      //history.push(`/my-hr/view-requests`);
    },
    onError: (error) => {
      console.log("send error message", error);
    },
  });

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleDelete = () => {
    deleteChangeRequest({
      variables: { _id: selectedId, locale: i18n.language },
    });
    setLoading(true);
  };

  const handleReject = () => {
    rejectChangeRequest({
      variables: { _id: selectedId, locale: i18n.language, message },
    });
    setLoading(true);
  };

  const filterByTypeGroup = (typeGroup: string, typeGroupId: string) =>
    allDataTypeGroup?.filter(
      (item: any) =>
        item?.typeGroup === typeGroup && item?.typeGroupId === typeGroupId,
    );

  const isExistNameFilter = filterByTypeGroup("name", typeGroupId);
  const isExistMotherNameFilter = filterByTypeGroup("mothersName", typeGroupId);
  const isExistPermanentAddressFilter = filterByTypeGroup(
    "permanentAddress",
    typeGroupId,
  );
  const isExistTemporaryAddressFilter = filterByTypeGroup(
    "temporaryAddress",
    typeGroupId,
  );
  const isExistServiceAddressFilter = filterByTypeGroup(
    "serviceAddress",
    typeGroupId,
  );

  const selectedItem = (
    code: string,
    array: Array<{ code: string; name?: string }>,
  ) => array.find((item) => item.code === code);

  const selectedCounty = (code: string) => selectedItem(code, countries);
  const oldSelectedCounty = selectedCounty(oldValue);
  const newSelectedCounty = selectedCounty(newValue);

  const selectedNationality = (code: string) =>
    selectedItem(code, nationalities);
  const oldNationality = selectedNationality(oldValue);
  const newNationality = selectedNationality(newValue);

  const handleApprove = () => {
    const params = {
      _id: selectedId,
      locale: i18n.language,
      message,
    };
    approveChangeRequest({ variables: params });
    setLoading(true);
  };

  const getExistFilter = (typeGroup: string) => {
    switch (typeGroup) {
      case "name":
        return isExistNameFilter;
      case "permanentAddress":
        return isExistPermanentAddressFilter;
      case "serviceAddress":
        return isExistServiceAddressFilter;
      case "temporaryAddress":
        return isExistTemporaryAddressFilter;
      case "mothersName":
        return isExistMotherNameFilter;
      default:
        return [];
    }
  };

  const getInvalidAddress = (type: string) => {
    switch (type) {
      case "permanentAddress":
        return permanentAddress;
      case "temporaryAddress":
        return temporaryAddress;
      case "serviceAddress":
        return serviceAddress;
      default:
        return [];
    }
  };

  const renderUserDataChangeBox = (item: any) => {
    if (Array.isArray(item)) {
      if (typeGroup === "invalidateAddress") {
        return (
          <Box width="640px">
            <OcInfoCard
              defaultLabel={t(type) + " " + t("MY_HR_INVALIDITON")}
              def={getInvalidAddress(type)}
              data={employeeData}
              editable={false}
            />
          </Box>
        );
      }
      return item.map((subItem, subIndex) => {
        const { type, oldValue, newValue } = subItem;
        return (
          <Box key={`${subItem}-${subIndex}`}>
            <MyHrDataChangeBox
              type={type}
              oldValue={
                type === "country" ? selectedCounty(oldValue)?.name : oldValue
              }
              newValue={
                type === "country" ? selectedCounty(newValue)?.name : newValue
              }
            />
            <Box style={{ margin: "16px 0" }} />
          </Box>
        );
      });
    }
  };

  if (type === "nationality") {
    oldValue = oldNationality?.name;
    newValue = newNationality?.name;
  }

  if (type === "dateOfBirth") {
    oldValue = new Date(oldValue).toLocaleDateString();
    newValue = new Date(newValue).toLocaleDateString();
  }

  if (type === "countryOfBirth") {
    oldValue = oldSelectedCounty?.name;
    newValue = newSelectedCounty?.name;
  }

  if (oldValue === "not_defined") {
    oldValue = "";
  }

  const handleButtonClick = () => {
    handleDelete()
    setShowConfirmDialog(false);
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  const handleRejectedButtonClick = () => {
    handleReject()
    setShowRejectedConfirmDialog(false);
  };

  const handleRejectedDialogCancel = () => {
    setShowRejectedConfirmDialog(false);
  };

  return (
    <>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleButtonClick}
        onCancel={handleDialogCancel}
        title={t("OC_DELETE_CONFIRM")}
        texts={[t("MY_HR_DELETE_CHANGE_REQUEST_TEXT")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <OcConfirmationDialog
        showDialog={showRejectedConfirmDialog}
        onClose={handleRejectedButtonClick}
        onCancel={handleRejectedDialogCancel}
        title={t("MY_HR_REJECTED_CHANGE_REQUEST")}
        texts={[t("MY_HR_REJECTED_CHANGE_REQUEST_TEXT")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <MyHrDataEditCollapseHeader
        expanded={expanded}
        handleExpandClick={handleExpandClick}
        type={type}
        createdDateAt={createdDateAt}
        approvedAt={approvedAt}
        rejectedAt={rejectedAt}
        typeGroup={typeGroup}
        isMobile={isMobile}
      />
      <Collapse in={expanded} timeout="auto">
        <Box className={classes.backgroundColor} padding="0px 16px">
          <Box className={classes.topBorder} />
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            padding="16px 0px 6px 0px"
          >
            <MyHrDataEditCardItem
              label={t("OC_REQUEST_CREATED_AT")}
              value={createdDateAt}
              isMobile={isMobile}
            />
            <MyHrDataEditCardItem
              label={t("MY_HR_DATE_OF_VALIDITY")}
              value={validDateFrom}
              isMobile={isMobile}
            />
          </Box>
          <Box className={classes.separatorBorder} />
          <Box padding="16px 16px 0px 16px">
            {renderUserDataChangeBox(getExistFilter(typeGroup))}
            {(!typeGroup ||
              typeGroup === "phones" ||
              typeGroup === "emails") && (
                <>
                  <MyHrDataChangeBox
                    type={type}
                    oldValue={oldValue}
                    newValue={newValue}
                  />
                  <Box height="16px" />
                </>
              )}
          </Box>
          <Box height="16px" />
          <Box maxWidth="640px" marginLeft={isMobile ? "0px" : "16px"}>
            {approvedAt === null && rejectedAt === null && (
              <>
                <MyHrDataRequesAttachment
                  employeeCode={employeeCode}
                  approvedAt={approvedAt}
                  rejectedAt={rejectedAt}
                  typeGroup={typeGroup}
                  type={type}
                  isMobile={isMobile}
                />
                <Box height="16px" />
              </>
            )}
            <OcRequestsMessagesCard
              messages={messages}
              module="MyHr"
              typeGroup={typeGroup}
              type={type}
              selectedId={selectedId}
              typeGroupId={typeGroupId}
              disabled={disabled}
              isMobile={isMobile}
              data={employeeData}
              loading={loading}
              role={isAdmin ? "admin" : "user"}
            />
          </Box>
          <Collapse
            in={rejectedAt === null && approvedAt === null && !isAdmin}
            timeout="auto"
          >
            <Box padding="0px 16px">
              <OcCardButtonBar
                handleLeftButton={() => setShowConfirmDialog(true)}
                leftButtonColor="primary"
                leftButtonText={t("OC_DELETE")}
                rightButtonColor="secondary"
                leftButtonDisabled={loading}
                leftButtonId={
                  typeGroup === "name" || typeGroup === "mothersName"
                    ? `delete-button-${typeGroup}`
                    : `delete-button-${type}`
                }
                handleRightButton={handleExpandClick}
                rightButtonText={t("MY_HR_LOCKUP")}
                rightButtonDisabled={loading}
                rightButtonId={`close-button-${type}`}
              />
            </Box>
          </Collapse>
          <Collapse
            in={rejectedAt === null && approvedAt === null && isAdmin}
            timeout="auto"
          >
            <Box padding="0px 16px">
              <OcCardButtonBar
                handleLeftButton={() => setShowRejectedConfirmDialog(true)}
                rightButtonColor="secondary"
                leftButtonColor="primary"
                leftButtonText={t("OC_REJECT")}
                leftButtonDisabled={loading || disabled}
                leftButtonId="reject-button"
                handleRightButton={handleExpandClick}
                rightButtonText={t("OC_CANCEL")}
                rightButtonDisabled={loading}
                rightButtonId="cancel-button"
                handlePrimaryButton={handleApprove}
                primaryButtonText={t("OC_APPROVE")}
                primaryButtonDisabled={loading || disabled}
                primaryButtonId="approve-button"
              />
            </Box>
          </Collapse>
        </Box>
      </Collapse>
    </>
  );
};

export default MyHrDataEditCollapse;
